import CommonIcons from "@base/assets/icons";
import CommonImages from "@base/assets/images";
import CommonStyles from "@components/CommonStyles";
import { Divider } from "antd";
import { IReAppointment } from "@base/modules/appointment/appointment.interface";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { createSearchParams, useNavigate } from "react-router-dom";
import TagReAppointmentStatus from "@components/CommonStyles/TagReAppointmentStatus";
import useToggle from "@base/hooks/useToggle";
import { baseRoutes } from "@base/routes/baseRoutes";

interface IProps {
  appointment: IReAppointment;
}

/** A follow-up can be booked while its day is still ahead (spec E8). */
const isUpcoming = (date?: string) => !!date && dayjs(date, "YYYY-MM-DD").isAfter(dayjs(), "day");

export default function ReAppointmentListItem({ appointment }: IProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open, shouldRender, toggle } = useToggle();

  const book = (type: "online" | "offline") => {
    toggle();
    navigate({
      pathname: baseRoutes.bookingAppointment,
      search: createSearchParams({
        type,
        reappointment: "1",
        ...(appointment.patient_record?.code ? { record_code: appointment.patient_record.code } : {}),
        ...(appointment.site_code ? { site_code: appointment.site_code } : {}),
        ...(appointment.date_of_appointment ? { date: appointment.date_of_appointment } : {}),
      }).toString(),
    });
  };

  const renderOption = (type: "online" | "offline", icon: React.ReactNode, title: string, description: string) => (
    <button
      type="button"
      onClick={() => book(type)}
      className="flex items-center gap-3 p-3 rounded-xl border border-common-divider bg-common-white text-left hover:border-common-primary"
    >
      <div className="shrink-0">{icon}</div>
      <div className="grow flex flex-col gap-0.5">
        <span className="text-common-maintext text-sm font-semibold font-['Inter']">{title}</span>
        <span className="text-common-subtext text-xs font-['Inter']">{description}</span>
      </div>
      <CommonIcons.RightOutlined className="text-common-subtext" />
    </button>
  );

  return (
    <div className="w-full bg-common-background p-3 rounded-2xl">
      <div className="flex justify-between items-center gap-3">
        <div className="min-w-0 flex flex-col gap-0.5">
          <span className="text-black text-base font-medium font-['Inter'] line-clamp-1">{appointment.patient_record?.patient_name}</span>
          <span className="text-common-subtext text-xs font-normal font-['Inter'] line-clamp-1">
            {[
              appointment.patient_record?.patient_gender === false ? t("appointmentDetail.female") : t("appointmentDetail.male"),
              appointment.patient_record?.patient_dob ? dayjs(appointment.patient_record.patient_dob, "YYYY-MM-DD").format("DD/MM/YYYY") : null,
            ]
              .filter(Boolean)
              .join(" - ")}
          </span>
        </div>

        <div>
          <TagReAppointmentStatus appointment={appointment} />
        </div>
      </div>

      <Divider className="my-4" />

      <div className="flex justify-between items-start gap-4">
        <div className="flex items-start gap-4 min-w-0 max-lg:w-full">
          <img className="max-lg:flex-non h-[125px]" src={CommonImages.DefaultDoctorAvatar} alt="" />
          <div className="flex flex-col justify-between min-h-[125px] max-lg:flex-row max-lg:grow max-sm:flex-col">
            <div className="flex flex-col gap-1 max-lg:grow">
              <div className="text-common-maintext text-base font-bold font-['Inter'] max-sm:line-clamp-1">{appointment.site_name}</div>
              <div className="text-common-subtext text-base font-normal font-['Inter'] max-sm:line-clamp-1">{appointment.address}</div>
              <div className="text-common-subtext text-base font-normal font-['Inter'] max-sm:line-clamp-1">{appointment.notes}</div>

              <div className="lg:hidden">
                <div className="text-common-subtext text-xs font-normal font-['Inter']">
                  {dayjs(appointment.date_of_appointment, "YYYY-MM-DD").format("DD/MM/YYYY")}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-lg:hidden">
          <CommonStyles.DayCalendarUI value={dayjs(appointment.date_of_appointment, "YYYY-MM-DD").format("DD/MM/YYYY")} format="DD/MM/YYYY" />
        </div>
      </div>

      {isUpcoming(appointment.date_of_appointment) ? (
        <div className="flex justify-end mt-3">
          <CommonStyles.ButtonUI onClick={toggle}>{t("user.appointment.btnApptBooking")}</CommonStyles.ButtonUI>
        </div>
      ) : null}

      {shouldRender && (
        <CommonStyles.ModalConfirmationUI
          open={open}
          toggle={toggle}
          type="normal"
          hiddenAction
          title={t("booking.reappointment.title")}
          content={
            <div className="flex flex-col gap-3">
              {renderOption("online", <CommonIcons.IconCameraBox />, t("booking.reappointment.online"), t("booking.reappointment.onlineDescription"))}
              {renderOption(
                "offline",
                <CommonIcons.IconFacilityBox />,
                t("booking.reappointment.offline"),
                t("booking.reappointment.offlineDescription"),
              )}
            </div>
          }
        />
      )}
    </div>
  );
}
